import { useLocation } from '@remix-run/react';
import CZ from 'country-flag-icons/string/1x1/CZ';
import DE from 'country-flag-icons/string/1x1/DE';
import GB from 'country-flag-icons/string/1x1/GB';
import PL from 'country-flag-icons/string/1x1/PL';
import SK from 'country-flag-icons/string/1x1/SK';
import React from 'react';
import { Dropdown } from 'react-bootstrap';

import Icon from '~/components/icons/Icon';
import { useSlugTranslations } from '~/context/SlugTranslationsContext';
import { locales as configLocales, defaultLocale as routesDefaultLocale } from '~/locales/routes.json';
import { detectRouteKeyFromPathname, convertPathToLanguage } from '~/utils/routes';
import { useChannelConfig } from '~/utils/useChannelConfig';

// Per-country string modules: the `react/1x1` entry is one module with all ~260 flags as
// top-level forwardRef calls, so any import of it lands the whole set in the layout chunk.
const localeFlags: Record<string, string> = {
  cs: CZ,
  en: GB,
  sk: SK,
  de: DE,
  pl: PL,
};

const Flag = ({ locale, className }: { locale: string; className: string }) => {
  const svg = localeFlags[locale];
  return svg ? <span className={className} dangerouslySetInnerHTML={{ __html: svg }} /> : null;
};

const LanguageSwitcher = () => {
  const { pathname, search, hash } = useLocation();
  const { slugTranslations } = useSlugTranslations();

  const channelData = useChannelConfig();

  const locales = channelData.locales;
  const localesWithoutSpecification = locales.map((loc) => loc.split('_')[0]);
  const validLocales = configLocales.filter((loc) => localesWithoutSpecification.includes(loc));

  // Detect locale from pathname - check if first segment is a known locale
  // If no locale prefix, use default locale from routes.json (cs)
  const segments = pathname.split('/').filter(Boolean);
  const firstSegment = segments[0];
  const localeFromPath = configLocales.includes(firstSegment) ? firstSegment : routesDefaultLocale;

  const info = detectRouteKeyFromPathname(pathname);
  const currentLocale = info?.locale ?? localeFromPath;

  const switchTo = (lang: string) => {
    const newPath = convertPathToLanguage(pathname, lang, slugTranslations);
    // Full page reload to ensure all data is refetched with the new locale. The query string
    // carries state the page needs (e.g. the thank-you page's ?token= once the cart cookie is gone).
    window.location.href = `${newPath}${search}${hash}`;
  };

  return (
    <Dropdown className="header__switcher header__switcher--language">
      <Dropdown.Toggle variant="link" className="no-caret">
        <Flag locale={currentLocale} className="flag-icon" />
        <span>{currentLocale.toUpperCase()}</span>
        <Icon name="chevron-down" size={20} className="tabler-icon-chevron-down" />
      </Dropdown.Toggle>

      <Dropdown.Menu className="p-1 mt-1">
        {validLocales.map((lang) => {
          return (
            <Dropdown.Item key={lang} active={currentLocale === lang} onClick={() => switchTo(lang)}>
              <Flag locale={lang} className="flag-icon" />
              {lang.toUpperCase()}
            </Dropdown.Item>
          );
        })}
      </Dropdown.Menu>
    </Dropdown>
  );
};

export default LanguageSwitcher;
